import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, User } from 'lucide-react';
import { companyInfo } from '../mock';
import { auth } from '../lib/firebaseClient';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'sonner';

const ToolLayout = ({ children, title }) => {
  const [currentUser, setCurrentUser] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user && window.location.pathname !== '/tools/auth') {
        navigate('/tools/auth');
      }
    });
    return () => unsub();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged Out', {
        description: 'You have been successfully logged out',
      });
      navigate('/tools/auth');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout Failed', {
        description: 'Failed to log out. Please try again.',
      });
    }
  };

  // Determine back button destination and text
  const isToolsHub = location.pathname === '/tools';
  const backPath = isToolsHub ? '/' : '/tools';
  const backText = isToolsHub ? 'Back to Home Page' : 'Back to Tools';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-dark)' }}>
      {/* Navigation Header */}
      <nav className="border-b" style={{ borderColor: 'rgba(9, 166, 163, 0.2)', backgroundColor: 'rgba(36, 37, 38, 0.95)' }}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
            </Link>

            {/* User Info */}
            <div className="flex items-center gap-4">
              {currentUser && (
                <>
                  <div className="flex items-center gap-2 text-white">
                    <User size={20} className="text-accent-teal" />
                    <span className="text-sm hidden md:block">{currentUser.email}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-accent-teal text-accent-teal hover:bg-accent-teal hover:text-white transition-all duration-300"
                  >
                    <LogOut size={18} />
                    <span className="hidden md:block">Logout</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        {/* Back Button */}
        <Link
          to={backPath}
          className="inline-flex items-center gap-2 text-accent-teal hover:text-white transition-colors duration-300 mb-8"
        >
          <ArrowLeft size={20} />
          <span>{backText}</span>
        </Link>

        {/* Title */}
        {title && (
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {title}
          </h1>
        )}

        {/* Content */}
        {children}
      </main>

      {/* Footer */}
      <footer className="mt-20 py-8 border-t" style={{ borderColor: 'rgba(9, 166, 163, 0.1)' }}>
        <div className="container mx-auto px-6 text-center text-neutral-silver">
          <p>© {new Date().getFullYear()} {companyInfo.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ToolLayout;
